/**
 * MapView Component - Mobile
 * 
 * Renders Mapbox GL JS map via iframe.
 * CRITICAL: Map HTML is STATIC - markers are sent via postMessage.
 * This prevents iframe reloads when opportunity data changes.
 * 
 * Exposes flyTo, fitBounds, zoomIn, zoomOut methods via ref.
 * Uses design tokens exclusively.
 */

import React, { 
  useMemo, 
  useCallback, 
  useEffect, 
  useRef, 
  useImperativeHandle, 
  forwardRef,
  useState,
} from 'react';
import { View, Text, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, icon } from '../../../shared/tokens/mobile';
import { MapOpportunity, BasemapStyle } from '../../../hooks/useMapData';
import { OpportunityStatus } from '../../../shared/types/opportunity';
import { buildMapHtml, BASEMAP_STYLES } from './mapHtmlBuilder';

interface MapViewProps {
  opportunities: MapOpportunity[];
  selectedOpportunity: MapOpportunity | null;
  mapboxToken: string | null;
  center: [number, number];
  zoom: number;
  basemap: BasemapStyle;
  onMarkerClick: (opportunity: MapOpportunity | null) => void;
  onLongPress?: (coords: { latitude: number; longitude: number }) => void;
  onDroppedPinClick?: (coords: { latitude: number; longitude: number }) => void;
  onDroppedPinCancel?: () => void;
  onMapReady?: () => void;
  onPositionChange?: (center: [number, number], zoom: number) => void;
  loading?: boolean;
}

export interface ParcelData {
  id: string | number;
  name?: string;
  geometry: object; // GeoJSON geometry
  acreage?: number | null;
  taxValue?: number | null;      // Tax assessed value from county
  askingPrice?: number | null;   // Asking/listing price
}

export interface MapViewRef {
  flyTo: (lat: number, lng: number, zoom?: number, showLocation?: boolean) => void;
  fitBounds: (bounds: [number, number, number, number]) => void;
  fitAllMarkers: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  showDroppedPin: (lat: number, lng: number) => void;
  removeDroppedPin: () => void;
  showParcels: (parcels: ParcelData[], neighborsByTier?: Record<number, ParcelData[]>) => void;
  hideParcels: () => void;
}

function getStatusColor(status: OpportunityStatus): string {
  switch (status) {
    case 'evaluating': return color.info;
    case 'under_contract': return color.warning;
    case 'owned': return color.success;
    case 'selling': return color.warning;
    case 'sold': return color.textMuted;
    case 'passed': return color.disabled;
    default: return color.primary;
  }
}

export const MapView = forwardRef<MapViewRef, MapViewProps>(function MapView({
  opportunities,
  selectedOpportunity,
  mapboxToken,
  center,
  zoom,
  basemap,
  onMarkerClick,
  onLongPress,
  onDroppedPinClick,
  onDroppedPinCancel,
  onMapReady,
  onPositionChange,
  loading,
}, ref) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [mapReady, setMapReady] = useState(false);
  
  // Store initial values - map HTML should be static
  const initialBasemap = useRef(basemap);
  const initialCenter = useRef(center);
  const initialZoom = useRef(zoom);

  // Build STATIC map HTML (no marker data)
  const mapHtml = useMemo(() => {
    if (!mapboxToken) return '';
    return buildMapHtml({
      mapboxToken,
      styleUrl: BASEMAP_STYLES[initialBasemap.current],
      center: initialCenter.current,
      zoom: initialZoom.current,
    });
  }, [mapboxToken]); // Only depends on token!

  // Convert opportunities to marker data format
  const markerData = useMemo(() => 
    opportunities.map(opp => ({
      id: opp.id,
      coords: [opp.longitude, opp.latitude] as [number, number],
      color: getStatusColor(opp.status),
      portfolio_id: opp.portfolio_id,
    })), 
    [opportunities]
  );

  // Send markers to iframe when data changes OR when map becomes ready
  useEffect(() => {
    if (!mapReady || Platform.OS !== 'web' || !iframeRef.current?.contentWindow) return;
    
    iframeRef.current.contentWindow.postMessage({
      type: 'update_markers',
      markers: markerData,
    }, '*');
  }, [markerData, mapReady]);

  // Send basemap changes
  useEffect(() => {
    if (!mapReady || Platform.OS !== 'web' || !iframeRef.current?.contentWindow) return;
    
    iframeRef.current.contentWindow.postMessage({
      type: 'change_basemap',
      basemap,
    }, '*');
  }, [basemap, mapReady]);

  // Send selection changes
  useEffect(() => {
    if (!mapReady || Platform.OS !== 'web' || !iframeRef.current?.contentWindow) return;
    
    iframeRef.current.contentWindow.postMessage({
      type: 'select_marker',
      id: selectedOpportunity?.id ?? null,
    }, '*');
  }, [selectedOpportunity, mapReady]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    flyTo: (lat: number, lng: number, zoomLevel?: number, showLocation?: boolean) => {
      if (Platform.OS === 'web' && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ 
          type: 'fly_to', 
          lat, 
          lng, 
          zoom: zoomLevel ?? 14,
          showLocation: showLocation ?? false
        }, '*');
      }
    },
    fitBounds: (bounds: [number, number, number, number]) => {
      if (Platform.OS === 'web' && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: 'fit_bounds', bounds }, '*');
      }
    },
    fitAllMarkers: () => {
      if (opportunities.length === 0) return;
      if (Platform.OS === 'web' && iframeRef.current?.contentWindow) {
        const lngs = opportunities.map(o => o.longitude);
        const lats = opportunities.map(o => o.latitude);
        const bounds: [number, number, number, number] = [
          Math.min(...lngs), Math.min(...lats),
          Math.max(...lngs), Math.max(...lats)
        ];
        iframeRef.current.contentWindow.postMessage({ type: 'fit_bounds', bounds }, '*');
      }
    },
    zoomIn: () => {
      if (Platform.OS === 'web' && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: 'zoom_in' }, '*');
      }
    },
    zoomOut: () => {
      if (Platform.OS === 'web' && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: 'zoom_out' }, '*');
      }
    },
    showDroppedPin: (lat: number, lng: number) => {
      if (Platform.OS === 'web' && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: 'show_dropped_pin', lat, lng }, '*');
      }
    },
    removeDroppedPin: () => {
      if (Platform.OS === 'web' && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: 'remove_dropped_pin' }, '*');
      }
    },
    showParcels: (parcels: ParcelData[], neighborsByTier: Record<number, ParcelData[]> = {}) => {
      if (Platform.OS === 'web' && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ 
          type: 'show_parcels', 
          parcels, 
          neighborsByTier 
        }, '*');
      }
    },
    hideParcels: () => {
      if (Platform.OS === 'web' && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: 'hide_parcels' }, '*');
      }
    },
  }), [opportunities]);

  // Handle messages from iframe
  const handleMessage = useCallback((event: MessageEvent) => {
    const data = event.data;
    if (!data?.type) return;
    
    switch (data.type) {
      case 'map_ready':
        setMapReady(true);
        // Send initial markers
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage({
            type: 'init_markers',
            markers: markerData,
          }, '*');
        }
        onMapReady?.();
        break;
        
      case 'marker_click':
        const opp = opportunities.find(o => o.id === data.id);
        if (opp) onMarkerClick(opp);
        break;
        
      case 'map_click':
        onMarkerClick(null);
        break;
        
      case 'long_press':
        onLongPress?.({ latitude: data.latitude, longitude: data.longitude });
        break;
        
      case 'dropped_pin_click':
        onDroppedPinClick?.({ latitude: data.latitude, longitude: data.longitude });
        break;
        
      case 'dropped_pin_cancelled':
        onDroppedPinCancel?.();
        break;
        
      case 'map_position_changed':
        onPositionChange?.(data.center, data.zoom);
        break;
    }
  }, [opportunities, onMarkerClick, onLongPress, onDroppedPinClick, onDroppedPinCancel, onMapReady, onPositionChange, markerData]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }
  }, [handleMessage]);

  // Loading state
  if (loading) {
    return (
      <View style={styles.placeholder}>
        <ActivityIndicator size="large" color={color.primary} />
        <Text style={styles.placeholderText}>Loading map...</Text>
      </View>
    );
  }

  // No token
  if (!mapboxToken) {
    return (
      <View style={styles.placeholder}>
        <Ionicons name="map-outline" size={icon.xxl} color={color.textMuted} />
        <Text style={styles.placeholderText}>Map unavailable</Text>
        <Text style={styles.placeholderSubtext}>Configure Mapbox to enable maps</Text>
      </View>
    );
  }

  // Web render
  if (Platform.OS === 'web') {
    return (
      <View style={styles.container}>
        <iframe
          ref={iframeRef as React.RefObject<HTMLIFrameElement>}
          srcDoc={mapHtml}
          style={{ width: '100%', height: '100%', border: 'none' }}
          title="Opportunities Map"
        />
      </View>
    );
  }

  // Native placeholder
  return (
    <View style={styles.placeholder}>
      <Ionicons name="map-outline" size={icon.xxl} color={color.textMuted} />
      <Text style={styles.placeholderText}>Map available on web</Text>
      <Text style={styles.placeholderSubtext}>Native maps coming soon</Text>
    </View>
  );
});

const styles = StyleSheet.create({
  container: { flex: 1 },
  placeholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: color.backgroundSecondary,
    gap: space.sm,
  },
  placeholderText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textMuted,
  },
  placeholderSubtext: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
  },
});
